import { LitElement, html, nothing } from 'lit';
import { RefiAnalytics } from '@cof/refi-web-sdk';
import { updateMicroStepEvent, displayProgressBarEvent, updateMacroStepEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import { DebugService } from './shared/services/debug.service.js';
import { SessionService } from './core/services/session/session.service.js';
import { AppService } from './app.service.js';
import { stratum, sharedAnalytics } from '../constants/stratum.js';
import { REFI_ESIGN_CONST } from '../constants/app.constants.js';
import './e-title/etitle.js';
import { FeatureToggleService } from './shared/services/feature-toggle.service.js';
import { ExperimentService } from './shared/services/experiment.service.js';
import { EsignService } from './shared/services/e-sign.service.js';
import { CookieService } from './shared/services/cookie.service.js';
import { HostAppEventsService } from './shared/services/host-app-events.service.js';
import './e-sign/contract-error/contract-error.component.js';
import { getIsV2Routing, setIsV2Routing } from './shared/services/routing-v2.service.js';
import { setTitleFollows } from './shared/services/title-follows.service.js';
import { setHasStip } from './shared/services/stip.service.js';

class RefiEsignApp extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiEsignApp');
    this.isPerf = false;
    this.stateCode = '';
    this.isETitle = false;
    let testingParam = '';
    this.experiments = {};
    if (window?.location) {
      testingParam = new URL(window.location.href)?.searchParams?.get('testing');
      this.experimentParam = new URL(window.location.href)?.searchParams?.get('experiment');
      this.featureToggleParam = new URL(window.location.href)?.searchParams?.get('feature');
      this.isPerf = testingParam === 'lighthouse' && !REFI_ESIGN_CONST.isProdEnv;
    }
    this.session = SessionService.session || {};
    if (this.isPerf) {
      this.session = SessionService.mockSession;
    }
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
    this.updateStratumMarketingChannel();
    this.loadingApp = false;
    this.existingExperimentIds = [];
    this.onProcessingPage = false;
    this.baseHref = document.querySelector('base').getAttribute('href');
    this._customerExperiences = AppService.customerExperiences !== undefined ? AppService.customerExperiences : [];
    displayProgressBarEvent(true);
    updateMacroStepEvent('Lock in payment', ['Offers', 'Review', 'Contract']);
    updateMicroStepEvent('Review');
    this.addEventListener(REFI_ESIGN_CONST.ROUTE_CHANGE, () => {
      this.requestUpdate();
    });
    this.analytics = new RefiAnalytics('e-sign', ['sitecatalyst', 'potomac'], appId);
    this.featureToggles = [];
    this.currentStep = '';
  }

  static get properties() {
    return {
      loadingApp: { type: Boolean },
      onProcessingPage: { type: Boolean },
      status: { type: String },
      featureToggles: { type: Object, attribute: 'feature-toggles' },
      experiments: { type: Object, attribute: 'experiments' },
      stateCode: { type: String, attribute: 'state-code' },
      isETitle: { type: Boolean, attribute: 'etitle' },
      preload: { type: String, attribute: 'pre-load' },
      currentStep: { type: String, attribute: 'current-step', reflect: true },
      applicationId: { type: String, attribute: 'application-id' },
      v2Routing: {
        type: Boolean,
        attribute: 'v2-routing',
        reflect: true,
        hasChanged: newVal => {
          setIsV2Routing(newVal);
        },
      },
      titleFollows: {
        type: Boolean,
        attribute: 'title-follows',
        reflect: true,
        hasChanged: newVal => {
          setTitleFollows(newVal);
        },
      },
      hasStip: {
        type: Boolean,
        attribute: 'has-stip',
        reflect: true,
        hasChanged: newVal => {
          setHasStip(newVal);
        },
      },
    };
  }

  firstUpdated() {
    HostAppEventsService.hideLoaderSpinnerEvent();
    sharedAnalytics.trackEvent({
      type: 'gen3-tracker',
      value: '',
      aggregator: 'qm',
      metadata: {},
    });
  }

  render() {
    this.debug.log('v2 routing:', getIsV2Routing());
    this.debug.log('RENDERED', this.baseHref, window.location.pathname);
    if (this.preload === 'true') {
      return nothing;
    }
    const contractError = ![405, 406, 407, 408].includes(this.session?.currentApp?.appStatusId);

    if (getIsV2Routing()) {
      if (contractError) {
        HostAppEventsService.sendNextStepEvent('contract-error');
      }
      switch (this.currentStep) {
        case 'e-sign':
          return this.getContractRouting();
        case 'contract-error':
          return html`<div role="main">
            <refi-contract-error></refi-contract-error>
          </div>`;
        default:
          return html``;
      }
    } else {
      if (contractError) {
        return html` <div role="main">
          <lit-route component="refi-contract-error" .resolve="${() => this.checkState('contract').then(() => import('./e-sign/e-sign.js'))}"></lit-route>
        </div>`;
      }
      return this.getContractRouting();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    // if testing, use qsp
    if (!REFI_ESIGN_CONST.isProdEnv) {
      if (this.experimentParam) {
        this.experiments = {
          'gap-messaging-refinement': {
            allocation: this.experimentParam,
            isEligible: true,
          },
        };
      }
      if (this.featureToggleParam) {
        this.featureToggles = [...this.featureToggles, { featureKey: this.featureToggleParam, isEnabled: true }];
      }
    }
    this.loadingApp = true;
    if (!this.isPerf) {
      this.session = SessionService.session || {};
    }
    const appId = this.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
    displayProgressBarEvent(true);
    if (this.featureToggles?.length) {
      FeatureToggleService.setFeatureToggles(this.featureToggles);
    }
    if (SessionService.isMockSession) {
      AppService.updateState({ isETitle: this.isETitle });
      this.loadingApp = false;
      // eslint-disable-next-line eqeqeq
    } else if (this.preload != 'true') {
      // send off request to get contract w/ pdfs
      EsignService.fetchContract(false, false);
      // request contract details and render page in the meantime
      EsignService.fetchContract(false, true)
        .then(() => {
          AppService.updateState({ isETitle: this.isETitle });
          this.loadingApp = false;
        })
        .catch(error => {
          this.debug.log(`Error was caught: ${JSON.stringify(error)}`);
          this.loadingApp = false;
          HostAppEventsService.sendGenericEvent('show-error-page');
        });
    }
  }

  getContractRouting() {
    return this.loadingApp
      ? html`<refi-loading-spinner message="We’re checking to see if your contract is ready" class="_gb-show"></refi-loading-spinner>`
      : html`<div role="main"><lit-route component="refi-contract" .resolve="${() => this.checkState('contract').then(() => import('./e-sign/e-sign.js'))}"></lit-route></div>`;
  }

  updateStratumMarketingChannel() {
    const parsedCookie = CookieService.getCookie(REFI_ESIGN_CONST.MARKETING_ATTRIBUTE_COOKIE)?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1]; // oldest non-bau attribution
    if (parsedCookie?.[0] === 'external_id') {
      // sets var to query param external id if first time with offer code, otherwise cookie
      stratum.setExternalVar('marketingChannel', externalIdFromCookie ?? SessionService.getExternalId());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.preload = false;
  }

  updated(changes) {
    super.updated(changes);
    if (changes.has('applicationId') && this.applicationId) {
      stratum.setExternalVar('applicationId', this.applicationId);
      this.updateStratumMarketingChannel();
    }
    if (changes.has('isETitle')) {
      AppService.updateState({ isETitle: this.isETitle }, true);
    }
    if (changes.has('stateCode')) {
      AppService.updateState({ stateCode: this.stateCode }, true);
    }
    if (changes.has('featureToggles') && this.featureToggles !== null && this.featureToggles.length !== 0) {
      FeatureToggleService.setFeatureToggles(this.featureToggles);
    }
    if (changes.has('experiments') && this.experiments !== null) {
      // loop through this.experiments and add the object name to experimentIds array if it's eligible
      const experimentIds = [];
      Object.keys(this.experiments).forEach(experiment => {
        if (this.experiments[experiment].isEligible) {
          experimentIds.push(this.experiments[experiment].allocation);
        } else {
          experimentIds.push(`${experiment}-ineligible`);
        }
      });
      ExperimentService.updateExperiments(experimentIds, this.existingExperimentIds);
      this.existingExperimentIds = experimentIds;
      const gapEducationExperiment = this.experiments['gap-education-rollout'];
      const gapMessagingExperiment = this.experiments['gap-messaging-refinement'];
      ExperimentService.gapEducationVariant = gapEducationExperiment?.isEligible ? gapEducationExperiment.allocation : null;
      ExperimentService.gapMessagingRefinementVariant = gapMessagingExperiment?.isEligible ? gapMessagingExperiment.allocation : null;
    }
  }

  async checkState() {
    return Promise.resolve();
  }
}

customElements.define('refi-esign-app', RefiEsignApp);
